import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AgilityRiskService } from '../../providers/agility-risk.service';
import { TranslateService } from '@ngx-translate/core';
import { EntityType } from '../../models/entity-type';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogService } from 'primeng/dynamicdialog';
import { RiskDistributionThreatsModalComponent } from './modals/risk-distribution-threats-modal/risk-distribution-threats-modal.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormControlModel, REQUIRED_CTRLS } from './config/risk-distribution-form-controls.config';
import { DristributionDropdownListModel } from './models/dropdown-list.model';
import { filter, map } from 'rxjs/operators';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { Threat } from '../../models/threat';
import { Contact } from '../../models/contact';
import { Table } from 'primeng/table';
import { AssessmentDistributionRequest } from '../../models/assessment-distribution-request';
import { SelectUsersModalComponent } from '../shared/modals/select-users-modal/select-users-modal.component';
import { AssessmentTarget } from '../../models/assessment-target';
import { Assessment } from '../../models/assessment';
import { RiskFormulaFilter } from '../../models/risk-formula-filter';
import { PageRequest } from '../../models/page-request';
import { AgilityRiskSpinnerService } from '../../providers/agility-risk-spinner.service';
import { NameEntityTypeFilter } from '../../models/name-entity-type-filter';
import { AgilityRiskMessageService } from '../../providers/agility-risk-message.service';
import { DistributionSendAssessmentNameModalComponent } from './modals/distribution-send-assessment-name-modal/distribution-send-assessment-name-modal.component';
import { RiskFormulaStatus } from '../../models/risk-formula-status';
import { AgilityRiskPermissionService } from '../../providers/agility-risk-permission.service';
import * as _ from 'lodash';

@Component({
  selector: 'lib-risk-distribution',
  templateUrl: './risk-distribution.component.html',
  styleUrls: ['./risk-distribution.component.scss'],
  providers: [
    DialogService
  ]
})
export class RiskDistributionComponent implements OnInit, OnDestroy {
  @ViewChild('surveyTargetTable') surveyTargetTable!: Table;
  requiredCtrls = REQUIRED_CTRLS;
  riskDistributionForm!: FormGroup;

  distributionDropdownLists: DristributionDropdownListModel = {
    assessmentType: {list: []},
    template: {list: []},
    formula: {list: []}
  };

  threatsDialogSubscription!: Subscription;
  assessmentTypeSubscription!: Subscription;
  targetContactsDialogSubscription!: Subscription;
  templateSubscription!: Subscription;

  threats: Threat[] = [];
  selectedThreats: Threat[] = [];
  surveyTargets: AssessmentTarget[] = [];
  selectedSurveyTargets: AssessmentTarget[] = [];
  riskAssessment: AssessmentDistributionRequest = new AssessmentDistributionRequest();

  minCalendarDate = new Date();

  constructor(
    private agilityRiskService: AgilityRiskService,
    public dialogService: DialogService,
    private fBuilder: FormBuilder,
    private translateService: TranslateService,
    private spinner: AgilityRiskSpinnerService,
    private messageService: AgilityRiskMessageService,
    private permissionService: AgilityRiskPermissionService) {
  }

  get canSubmitAssessments(): boolean {
    return this.riskDistributionForm.valid &&
      this.selectedSurveyTargets.length > 0 &&
      this.selectedThreats.length > 0 &&
      this.selectedSurveyTargets.findIndex(s => s.contacts.length === 0) === -1;
  }

  @HostListener('window:unload') pageUnloadHandler(): void {
    this.removeThreatsDialogLocalState();
  }

  ngOnInit(): void {
    this.selectedThreats = [];
    this.buildForm();
    this.fetchAssessmentType();
    this.onAssessmentTypeSelected();
    this.onTemplateSelected();
  }

  ngOnDestroy(): void {
    this.threatsDialogSubscription?.unsubscribe();
    this.assessmentTypeSubscription?.unsubscribe();
    this.targetContactsDialogSubscription?.unsubscribe();
    this.templateSubscription?.unsubscribe();
    this.removeThreatsDialogLocalState();
  }

  buildForm(): void {
    this.riskDistributionForm = this.fBuilder.group({});

    for (const ctrl of this.requiredCtrls) {
      this.riskDistributionForm.addControl(
        ctrl.key,
        this.fBuilder.control(null, ctrl.required ? Validators.required : null)
      );
    }
  }

  formCtrl(key: string): FormControl {
    return this.riskDistributionForm.get(key) as FormControl;
  }

  onAssessmentTypeSelected(): void {
    this.assessmentTypeSubscription = this.riskDistributionForm.get('assessmentType')
      .valueChanges.subscribe({
        next: assessmentType => {
          this.resetAssessment();
          this.fetchTargets(assessmentType);
          this.fetchRiskTemplates(assessmentType);
          this.fetchRiskFormulas(assessmentType);
        }
      });
  }

  onTemplateSelected(): void {
    this.templateSubscription = this.riskDistributionForm.get('template')
      .valueChanges.subscribe({
        next: template => template && this.fetchRiskTemplateThreats(template)
      });
  }

  shouldEnableCtrl(control: FormControlModel): boolean {
    return control?.dependsOn &&
      !this.riskDistributionForm.get(control.dependsOn)?.value;
  }

  fetchAssessmentType(): void {
    this.agilityRiskService.getRiskTemplateTargetEntityTypes().pipe(
      map(assessmentTypes => {
        const ASSESSMENT_TYPE_OBJ: { name: string; id: EntityType }[] = [];

        assessmentTypes.forEach(assessmentType => {
          ASSESSMENT_TYPE_OBJ.push({
            name: this.translateService.instant(
              `distribution.assessmentTypes.${assessmentType}`
            ),
            id: assessmentType
          });
        });
        return ASSESSMENT_TYPE_OBJ;
      })
    ).subscribe({
      next: assessmentTypes => {
        this.distributionDropdownLists.assessmentType.list = assessmentTypes;
      },
      error: (error: HttpErrorResponse) => {
        console.error(error.message);
      }
    });
  }

  fetchTargets(assessmentType: EntityType): void {
    const query = new NameEntityTypeFilter();
    query.entityTypeArea = assessmentType;
    const pageRequest = new PageRequest<NameEntityTypeFilter>({query});

    this.agilityRiskService.getAssessmentTargets(pageRequest).pipe(
      map(response => {
        const formattedTargets = _.cloneDeep(response.content).filter(t => this.permissionService.canEdit(t));

        formattedTargets.forEach((target: any) => {
          if (target.contacts?.length) {
            target.contacts.forEach(contact => {
              contact.name = contact.displayName;
            });

            target.recipients = this.formatTargetRecipientName(target.contacts);
          }
        });
        return formattedTargets;
      })
    ).subscribe({
      next: targets => this.surveyTargets = targets,
      error: (error: HttpErrorResponse) => console.error(error.message)
    });
  }

  formatTargetRecipientName(contacts: Contact[]): string {
    return contacts.map(contact => contact.name).join('; ');
  }

  fetchRiskTemplates(entityTypeArea: EntityType): void {
    this.agilityRiskService.getRiskTemplates({entityTypeArea}).subscribe({
      next: response => {
        this.distributionDropdownLists.template.list = response.content;
      },
      error: (error: HttpErrorResponse) => {
        console.error(error.message);
      }
    });
  }

  fetchRiskTemplateThreats(templateId: string): void {
    this.agilityRiskService.getThreats(templateId).subscribe({
      next: threats => {
        this.threats = threats;
        this.selectedThreats = threats;
      },
      error: (error: HttpErrorResponse) => console.error(error.message)
    });
  }

  removeThreatsDialogLocalState(): void {
    localStorage.removeItem('threat-modal-local');
  }

  fetchRiskFormulas(assessmentType: EntityType): void {
    const pageRequest1 = new PageRequest<RiskFormulaFilter>(
      {
        query: {
          status: RiskFormulaStatus.active,
          targetType: assessmentType
        }
      }
    );
    const pageRequest2 = new PageRequest<RiskFormulaFilter>(
      {
        query: {
          status: RiskFormulaStatus.active,
          targetType: 'ALL' as EntityType
        }
      }
    );

    forkJoin([
      this.agilityRiskService.getRiskFormulas(pageRequest1),
      this.agilityRiskService.getRiskFormulas(pageRequest2)
    ]).subscribe({
      next: ([list, list2]) => {
        this.distributionDropdownLists.formula.list = [...list.content, ...list2.content];
      },
      error: (error: HttpErrorResponse) => console.error(error.message)
    });
  }

  showThreatList(): void {
    if (!this.threats.length) {
      return;
    }

    const DIALOG_REF = this.dialogService.open(
      RiskDistributionThreatsModalComponent,
      {
        data: {
          threats: this.threats,
          selectedThreats: this.selectedThreats
        },
        header: this.translateService.instant('common.threats'),
        width: '70%'
      }
    );

    this.threatsDialogSubscription = DIALOG_REF.onClose
      .subscribe((selectedThreats: Threat[]) => {
        this.selectedThreats = selectedThreats || this.selectedThreats;
      });
  }

  resetAssessment(): void {
    this.resetDistributionForm();
    this.resetRiskTemplates();
    this.resetSurveyTargetTable();
  }

  resetDistributionForm(): void {
    for (const key in this.riskDistributionForm.controls) {
      if (key !== 'assessmentType') {
        this.riskDistributionForm.get(key).reset();
      }
    }
  }

  resetRiskTemplates(): void {
    this.threats = [];
    this.selectedThreats = [];
    this.removeThreatsDialogLocalState();
  }

  resetSurveyTargetTable(): void {
    this.surveyTargetTable.reset();
    this.selectedSurveyTargets = [];
  }

  showTargetContactList(target: AssessmentTarget): void {
    const DIALOG_REF = this.dialogService.open(
      SelectUsersModalComponent,
      {
        data: {selectedUsers: target.contacts}
      }
    );

    this.targetContactsDialogSubscription = DIALOG_REF.onClose.pipe(
      filter(selectedContacts => selectedContacts)
    ).subscribe((selectedContacts: Contact[]) => {
      const surveyTargetsClone = Object.assign([], this.surveyTargets);
      const targetClone = surveyTargetsClone.find(t => t.id === target.id);

      targetClone.recipients = this.formatTargetRecipientName(selectedContacts);
      this.surveyTargets = surveyTargetsClone;
    });
  }

  markFormAsInvalid(): void {
    Object.values(this.riskDistributionForm.controls).forEach(ctrl => {
      if (ctrl.invalid) {
        ctrl.markAllAsTouched();
        ctrl.markAsDirty();
      }
    });
  }

  noSurveyTargetSelectedErrorMessage(): void {
    const selectedTarget = this.riskDistributionForm.get('assessmentType').value;

    if (!selectedTarget || !!this.selectedSurveyTargets.length) {
      return;
    }

    const target = this.translateService.instant(
      `distribution.assessmentTypesPlural.${selectedTarget}`);

    this.messageService.error(
      this.translateService.instant('common.error'),
      this.translateService.instant('distribution.missingSurveyTarget', {target})
    );
  }

  noRecipientsAddedErrorMessage(): void {
    const noMissingRecipients = this.selectedSurveyTargets.every(target => {
      return !!target.contacts.length;
    });

    if (noMissingRecipients) return;

    const selectedTarget = this.riskDistributionForm.get('assessmentType').value;
    const target = this.translateService.instant(
      `distribution.assessmentTypesPlural.${selectedTarget}`);

    this.messageService.error(
      this.translateService.instant('common.error'),
      this.translateService.instant('distribution.missingSurveyRecipients', {target})
    );
  }

  isTargetSelected(target: AssessmentTarget): boolean {
    return this.selectedSurveyTargets.some(sTarget => {
      return sTarget.id === target.id;
    });
  }

  onSubmitAssessment(): void {
    if (!this.canSubmitAssessments) {
      this.markFormAsInvalid();
      this.noSurveyTargetSelectedErrorMessage();
      this.noRecipientsAddedErrorMessage();
      return;
    }

    // open modal allowing user to set the assessment name
    const distributionSendAssessmentNameModalRef = this.dialogService.open(
      DistributionSendAssessmentNameModalComponent,
      {
        header: this.translateService.instant('distribution.sendAssessments'),
        width: '50%'
      }
    );

    distributionSendAssessmentNameModalRef.onClose
      .pipe(filter(proceed => !!proceed))
      .subscribe((res: { name: string }) => {
        this.spinner.start();
        const assessmentRequests = new Array<Observable<Assessment>>();
        this.selectedSurveyTargets.forEach((surveyTarget: AssessmentTarget) => {
          const assessmentDistribution = new AssessmentDistributionRequest();
          assessmentDistribution.dueDate = this.formCtrl('dueDate').value as Date;
          assessmentDistribution.targetEntity = {id: surveyTarget.id, name: surveyTarget.name, entityType: this.formCtrl('assessmentType').value};
          assessmentDistribution.formula = {id: this.formCtrl('formula').value};
          assessmentDistribution.threats = this.selectedThreats.map(t => {
            return {id: t.id};
          });
          assessmentDistribution.users = surveyTarget.contacts.map(c => {
            return {id: c.id};
          });

          // set the name provided from modal, otherwise leave blank for default name
          if (res?.name.length) {
            assessmentDistribution.name = res.name;
          }

          assessmentRequests.push(this.agilityRiskService.createAssessmentAndSendDistribution(assessmentDistribution));
        });

        forkJoin(assessmentRequests).subscribe({
          next: (resp: Array<Assessment>) => {
            this.messageService.success(this.translateService.instant('common.success'), this.translateService.instant('distribution.sendDistributionSuccess'));
            this.spinner.stop();

            // reinit the page
            this.ngOnInit();
          },
          error: (err) => {
            this.messageService.error(this.translateService.instant('common.error'), this.translateService.instant('riskTemplates.sendDistributionFail'));
            this.spinner.stop();
          }
        });
      });
  }
}
